import {
  createContext,
  FC,
  forwardRef,
  MouseEventHandler,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import {
  FormControl,
  InputAdornment,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  useTheme,
} from '@mui/material';
import useCreateCompany from '../../hooks/api/useCreateCompany';
import { menuItemStyles } from '@utils/styles';
import AddOrEditCompanyModal from '../../pages/Dashboard/components/DealSetup/components/AddOrEditCompanyModal';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import useTanstackVirtualizer from '../../hooks/useTanstackVirtualizer';

const VIRTUAL_ITEM_HEIGHT = 40;
const VIRTUAL_MENU_MAX_HEIGHT = 300;

type MenuItemOption = { label: string; value: string };
const ITEM_BG_COLOR = '#F8F8F9';

const itemSx = {
  ...menuItemStyles,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  height: 'fit-content',
  boxSizing: 'border-box' as const,
  backgroundColor: ITEM_BG_COLOR,
  '&.Mui-selected': { backgroundColor: '#51B8B026 !important' },
  '&.Mui-selected:hover': { backgroundColor: '#51B8B04D !important' },
};

type VirtualSelectContextValue = {
  rows: MenuItemOption[];
  selectedValue: unknown;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  enableSearch: boolean;
  isAddNew: boolean;
  onSelect: (value: string) => void;
  onAddNew: () => void;
  onCloseMenu: () => void;
};

const VirtualSelectContext = createContext<VirtualSelectContextValue>({
  rows: [],
  selectedValue: '',
  searchQuery: '',
  onSearchChange: () => {},
  enableSearch: false,
  isAddNew: false,
  onSelect: () => {},
  onAddNew: () => {},
  onCloseMenu: () => {},
});

const VirtualMenuList = forwardRef<HTMLUListElement, any>(
  ({ children: _children, ...props }, ref) => {
    const {
      rows,
      selectedValue,
      searchQuery,
      onSearchChange,
      enableSearch,
      isAddNew,
      onSelect,
      onAddNew,
      onCloseMenu,
    } = useContext(VirtualSelectContext);

    const scrollRef = useRef<HTMLDivElement>(null);

    const { rowVirtualizer } = useTanstackVirtualizer({
      rows,
      parentRef: scrollRef as React.RefObject<HTMLDivElement>,
      itemHeight: VIRTUAL_ITEM_HEIGHT,
      hasNextPage: false,
      isFetchingNextPage: false,
      fetchNextPage: () => {},
    });

    return (
      <div style={{ color: '#162936' }}>
        {isAddNew && (
          <MenuItem
            onClick={() => {
              onCloseMenu();
              onAddNew();
            }}
            sx={{
              ...itemSx,
              color: '#51B8B0',
              display: 'flex',
              backgroundColor: ITEM_BG_COLOR,
            }}
          >
            <AddIcon sx={{ width: '18px', height: '18px' }} />
            Create New
          </MenuItem>
        )}
        {enableSearch && (
          <div style={{ padding: '8px', backgroundColor: ITEM_BG_COLOR }}>
            <InputBase
              autoFocus
              fullWidth
              placeholder="Search…"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#9E9E9E' }} />
                </InputAdornment>
              }
              sx={{
                fontSize: '14px',
                color: '#162936',
                backgroundColor: '#fff',
                borderRadius: '6px',
                border: '1px solid #d6d6d6',
                px: 1,
                py: '4px',
                '& input': { padding: '0 4px' },
              }}
            />
          </div>
        )}
        <div
          ref={scrollRef}
          style={{ overflow: 'auto', maxHeight: VIRTUAL_MENU_MAX_HEIGHT, height: 'fit-content' }}
        >
          <ul
            {...props}
            ref={ref}
            style={{
              height: rowVirtualizer.getTotalSize(),
              width: '100%',
              position: 'relative',
              padding: 0,
              margin: 0,
              listStyle: 'none',
              backgroundColor: ITEM_BG_COLOR,
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index];
              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <MenuItem
                    value={row.value}
                    selected={selectedValue === row.value}
                    onClick={() => {
                      onSelect(row.value);
                      onCloseMenu();
                    }}
                    sx={itemSx}
                  >
                    {row.label}
                  </MenuItem>
                </div>
              );
            })}
          </ul>
        </div>
      </div>
    );
  },
);
VirtualMenuList.displayName = 'VirtualMenuList';

type CustomSelectProps = {
  label: string;
  labelId: string;
  menuItems?: MenuItemOption[];
  Icon?: JSX.Element;
  defaultValue?: string;
  onClick?: MouseEventHandler;
  onChange?: (value: any) => void;
  isAddNew?: boolean;
  disabled?: boolean;
  field?: string;
  required?: boolean;
  selectStyles?: any;
  error?: boolean;
  onBlur?: () => void;
  enableSearch?: boolean;
  searchAlgorithm?: 'startsWith' | 'includes';
};

const SelectWithLabel: FC<CustomSelectProps & SelectProps> = (props) => {
  const {
    label,
    labelId,
    menuItems,
    Icon = <ArrowDropDownIcon />,
    defaultValue,
    onClick = () => {},
    onChange = () => {},
    isAddNew,
    value,
    disabled = false,
    field = 'id',
    required = false,
    selectStyles = {},
    error = false,
    onBlur,
    enableSearch = false,
    MenuProps: menuPropsFromParent = {},
    searchAlgorithm = 'includes',
    ...rest
  } = props;

  const theme = useTheme();
  const dispatch = useDispatch();
  const { addCompany } = useCreateCompany();

  const [openDialog, setOpenDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const handleCloseMenu = useCallback(() => {
    setMenuOpen(false);
    setSearchQuery('');
    onBlur?.();
  }, [onBlur]);

  const handleAddNewClick = useCallback(() => setOpenDialog(true), []);
  const handleDialogClose = () => setOpenDialog(false);

  const handleDialogSubmit = async (details: {
    name?: string;
    address?: string;
    companyHQ?: string;
  }) => {
    if (!details.name) {
      setOpenDialog(false);
      return;
    }
    try {
      const res = await addCompany({
        name: details.name,
        address: details.address || undefined,
        companyHQ: details.companyHQ || undefined,
      });
      if (!res) {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            message: 'Failed to create company. Please try again.',
          }),
        );
        return;
      }
      onChange(field !== 'id' ? res : res[field]);
      setOpenDialog(false);
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message:
            error?.detailedErrorObject?.message ||
            error?.error?.message ||
            error?.message ||
            'Failed to create company. Please try again.',
        }),
      );
    }
  };

  const selectedLabel = useMemo(() => {
    if (value == null || value === '') return '';
    return menuItems?.find((item) => item.value === value)?.label ?? String(value);
  }, [menuItems, value]);

  const rows = useMemo<MenuItemOption[]>(() => {
    if (!enableSearch || !searchQuery.trim()) return menuItems ?? [];
    const lowerQuery = searchQuery.toLowerCase();
    switch (searchAlgorithm) {
      case 'startsWith':
        return (menuItems ?? []).filter((item) => item.label.toLowerCase().startsWith(lowerQuery));
      case 'includes':
      default:
        return (menuItems ?? []).filter((item) => item.label.toLowerCase().includes(lowerQuery));
    }
  }, [menuItems, enableSearch, searchQuery]);

  const contextValue = useMemo<VirtualSelectContextValue>(
    () => ({
      rows,
      selectedValue: value,
      searchQuery,
      onSearchChange: setSearchQuery,
      enableSearch,
      isAddNew: !!isAddNew,
      onSelect: onChange,
      onAddNew: handleAddNewClick,
      onCloseMenu: handleCloseMenu,
    }),
    [
      rows,
      value,
      searchQuery,
      enableSearch,
      isAddNew,
      onChange,
      handleAddNewClick,
      handleCloseMenu,
    ],
  );

  const parentMenuProps = menuPropsFromParent as Record<string, any>;

  return (
    <>
      <FormControl fullWidth>
        <InputLabel
          required={required}
          id={labelId}
          sx={{
            color: '#6C6C6C',
            '&.Mui-focused': {
              color: error ? theme.palette.error.main : theme.palette.primary.main,
            },
          }}
        >
          {label}
        </InputLabel>
        <VirtualSelectContext.Provider value={contextValue}>
          <Select
            ref={selectRef}
            {...rest}
            label={label}
            required={required}
            labelId={labelId}
            onClick={onClick}
            defaultValue={defaultValue}
            value={value}
            disabled={disabled}
            error={error}
            open={menuOpen}
            onOpen={() => setMenuOpen(true)}
            onClose={handleCloseMenu}
            renderValue={() => selectedLabel}
            MenuProps={{
              ...parentMenuProps,
              PaperProps: {
                ...parentMenuProps?.PaperProps,
                style: {
                  width: selectRef.current?.getBoundingClientRect().width,
                  ...parentMenuProps?.PaperProps?.style,
                },
              },
              MenuListProps: {
                ...parentMenuProps?.MenuListProps,
                component: VirtualMenuList,
              },
            }}
            sx={{
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#d6d6d6', borderRadius: '8px' },
                '&:hover fieldset': { borderColor: '#d6d6d6' },
                '&.Mui-focused fieldset': { borderColor: '#d6d6d6' },
              },
              '.MuiSvgIcon-root ': { fill: '#2F736E !important' },
              ...selectStyles,
            }}
            {...((Icon && { IconComponent: () => Icon }) || {})}
          >
            {value != null && value !== '' && (
              <MenuItem value={value as string} sx={{ display: 'none' }}>
                {selectedLabel}
              </MenuItem>
            )}
          </Select>
        </VirtualSelectContext.Provider>
      </FormControl>
      <AddOrEditCompanyModal
        action="add"
        openDialog={openDialog}
        handleDialogClose={handleDialogClose}
        handleDialogSubmit={(newDetails) => handleDialogSubmit(newDetails)}
      />
    </>
  );
};

export default SelectWithLabel;
